import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Heart, Loader2, Lock, Repeat, Calendar, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AMOUNTS = [25, 50, 100, 250, 500, 1000];

const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One-time", desc: "Single donation" },
  { value: "daily", label: "Daily", desc: "Great for last 10 nights" },
  { value: "weekly", label: "Weekly", desc: "Every week" },
  { value: "monthly", label: "Monthly", desc: "Most popular" },
  { value: "quarterly", label: "Quarterly", desc: "Every 3 months" },
  { value: "annually", label: "Annually", desc: "Once a year" },
];

let stripePromiseCache = null;
async function getStripePromise() {
  if (stripePromiseCache) return stripePromiseCache;
  const { data } = await api.get("/donations/config");
  if (!data.publishable_key) return null;
  stripePromiseCache = loadStripe(data.publishable_key);
  return stripePromiseCache;
}

export default function Donate() {
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [designation, setDesignation] = useState("General Fund");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [coverFees, setCoverFees] = useState(false);
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [designations, setDesignations] = useState([]);

  const [clientSecret, setClientSecret] = useState(null);
  const [intentMode, setIntentMode] = useState(null);
  const [txId, setTxId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState(null); // null | stripePromise | false
  const paymentSectionRef = useRef(null);

  useEffect(() => {
    api.get("/donation-designations").then((r) => setDesignations(r.data)).catch(() => {});
    getStripePromise().then((p) => setStripeReady(p || false));
  }, []);

  // When the inline Stripe form mounts, scroll it into view smoothly so the
  // donor doesn't have to hunt for the new payment fields.
  useEffect(() => {
    if (clientSecret && paymentSectionRef.current) {
      // Small delay so Stripe Elements has time to render before we measure.
      const t = setTimeout(() => {
        paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [clientSecret]);

  const effectiveAmount = useMemo(() => {
    const a = customAmount ? Number(customAmount) : amount;
    return Number.isFinite(a) && a > 0 ? a : 0;
  }, [amount, customAmount]);

  const totalWithFees = useMemo(() => {
    if (!coverFees) return effectiveAmount;
    return ((effectiveAmount + 0.3) / (1 - 0.029)).toFixed(2);
  }, [effectiveAmount, coverFees]);

  const needsEmail = frequency !== "one_time";
  const showScheduleEnd = ["daily", "weekly", "monthly", "quarterly", "annually"].includes(frequency);

  const proceed = async () => {
    if (!effectiveAmount || effectiveAmount < 1) return toast.error("Enter an amount of at least $1");
    if (needsEmail && !donorEmail) return toast.error("Email is required for recurring donations");
    setLoading(true);
    try {
      const r = await api.post("/donations/intent", {
        amount: effectiveAmount,
        frequency,
        designation,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        cover_fees: coverFees,
        schedule_end: scheduleEnd || undefined,
      });
      setClientSecret(r.data.client_secret);
      setIntentMode(r.data.mode);
      setTxId(r.data.tx_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start donation");
    } finally { setLoading(false); }
  };

  if (stripeReady === null) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (stripeReady === false) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 py-20">
        <Heart className="text-gold mb-4" size={44} />
        <h1 className="font-serif text-3xl text-ink">Donations temporarily unavailable</h1>
        <p className="mt-3 text-ink/65 max-w-lg">The payment system is being configured. Please check back shortly, or call <a href="tel:+18104062617" className="text-navy underline">(810) 406-2617</a> to donate by phone.</p>
      </div>
    );
  }

  return (
    <div data-testid="donate-page">
      <section className="relative bg-navy text-cream py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-20" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold mb-4">
            <Heart size={12} className="fill-gold" /> Your support
          </div>
          <h1 className="display text-4xl md:text-6xl">Support Flint Islamic Center</h1>
          <p className="mt-6 max-w-2xl mx-auto text-cream/80 font-light leading-relaxed">
            "Whoever builds a masjid for Allah, Allah will build for him a similar house in Jannah" <span className="text-gold">— Sahih Muslim</span>
          </p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-10">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr,400px] gap-10">
          {/* LEFT — form */}
          <div className="space-y-8">
            {!clientSecret && (
              <>
                {/* Frequency */}
                <div>
                  <SectionTitle num="1">Choose frequency</SectionTitle>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    {FREQUENCY_OPTIONS.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => setFrequency(f.value)}
                        data-testid={`freq-${f.value}`}
                        className={`rounded-2xl border-2 p-4 text-left transition-all ${
                          frequency === f.value
                            ? "bg-navy text-cream border-navy shadow-lg"
                            : "bg-white border-sand text-ink hover:border-gold hover:-translate-y-0.5"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          {f.value === "one_time" ? <Heart size={14} /> : <Repeat size={14} />}
                          {f.label}
                        </div>
                        <div className={`text-xs mt-1 ${frequency === f.value ? "text-cream/75" : "text-ink/55"}`}>{f.desc}</div>
                      </button>
                    ))}
                  </div>
                  {showScheduleEnd && (
                    <div className="mt-4 rounded-2xl bg-gold/10 border border-gold/40 p-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-navy font-semibold flex items-center gap-2">
                        <Calendar size={12} /> End date (optional)
                      </label>
                      <input
                        type="date"
                        value={scheduleEnd}
                        onChange={(e) => setScheduleEnd(e.target.value)}
                        data-testid="schedule-end"
                        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand bg-white"
                      />
                      <div className="text-xs text-ink/60 mt-2">e.g. for the last 10 nights of Ramadan, set end date. Leave blank for ongoing.</div>
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <SectionTitle num="2">Choose amount</SectionTitle>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {AMOUNTS.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => { setAmount(v); setCustomAmount(""); }}
                        data-testid={`amount-${v}`}
                        className={`rounded-2xl border-2 py-4 text-center font-semibold transition-all ${
                          amount === v && !customAmount
                            ? "bg-navy text-cream border-navy shadow-lg"
                            : "bg-white border-sand text-ink hover:border-gold hover:-translate-y-0.5"
                        }`}
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Or custom amount</label>
                    <div className="mt-2 relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/50">$</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="100"
                        data-testid="custom-amount"
                        className="w-full pl-8 pr-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
                      />
                    </div>
                  </div>
                </div>

                {/* Designation */}
                <div>
                  <SectionTitle num="3">Choose cause</SectionTitle>
                  <select
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    data-testid="designation-select"
                    className="mt-4 w-full px-4 py-3 rounded-xl border border-sand bg-white focus:outline-none focus:border-navy"
                  >
                    {designations.map((d) => (
                      <option key={d.id || d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                  {designations.find((d) => d.name === designation)?.description && (
                    <p className="text-xs text-ink/55 mt-2">{designations.find((d) => d.name === designation)?.description}</p>
                  )}
                </div>

                {/* Donor info */}
                <div>
                  <SectionTitle num="4">Your information {!needsEmail && <span className="text-ink/50 text-xs normal-case font-normal tracking-normal">(optional)</span>}</SectionTitle>
                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <input type="text" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Your name" data-testid="donor-name" className="px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
                    <input type="email" required={needsEmail} value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} placeholder={needsEmail ? "Email (required for recurring)" : "Email (for receipt)"} data-testid="donor-email" className="px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
                  </div>
                </div>

                {/* Cover fees */}
                <label className="flex items-start gap-3 cursor-pointer rounded-2xl bg-cream border border-sand p-4">
                  <input type="checkbox" checked={coverFees} onChange={(e) => setCoverFees(e.target.checked)} data-testid="cover-fees" className="w-5 h-5 accent-navy mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Cover processing fees (recommended)</div>
                    <div className="text-xs text-ink/60 mt-0.5">Add ~3% so 100% of ${effectiveAmount.toFixed(2)} reaches FIC.</div>
                    <div className="text-[11px] text-ink/55 mt-2 leading-relaxed" data-testid="fee-note">
                      <strong className="text-ink/70">Processing fees:</strong>
                      <span className="block">Cards — 2.2% + 30¢ per transaction (American Express 3% + 50¢).</span>
                      <span className="block"><strong className="text-ink/70">ACH</strong> (recommended for larger gifts) — 0.80%, capped at $5 for any amount over $625.</span>
                    </div>
                  </div>
                </label>

                <button
                  onClick={proceed}
                  disabled={loading}
                  data-testid="continue-to-payment"
                  className="donate-cta w-full rounded-full py-5 text-sm font-bold uppercase tracking-[0.22em] text-navy disabled:opacity-60 inline-flex items-center justify-center gap-2.5 relative overflow-hidden group"
                >
                  <span className="donate-cta-shimmer" aria-hidden="true" />
                  {loading ? <Loader2 size={18} className="animate-spin relative z-[1]" /> : <Lock size={16} className="relative z-[1]" />}
                  <span className="relative z-[1]">Continue to secure payment · ${coverFees ? totalWithFees : effectiveAmount.toFixed(2)}</span>
                </button>
              </>
            )}

            {clientSecret && (
              <div ref={paymentSectionRef}>
                <Elements
                  stripe={stripeReady}
                  options={{
                    clientSecret,
                    appearance: { theme: "stripe", variables: { colorPrimary: "#0E2A47" } },
                    // Surface Apple Pay / Google Pay / Link as the FIRST payment
                    // methods inside the Stripe Payment Element. The methods
                    // themselves are auto-enabled by `automatic_payment_methods`
                    // on the backend; this controls the visual order.
                    paymentMethodOrder: ["apple_pay", "google_pay", "link", "card", "us_bank_account"],
                  }}
                >
                  <InlineCheckout
                    mode={intentMode}
                    txId={txId}
                    onCancel={() => { setClientSecret(null); setTxId(null); }}
                  />
                </Elements>
              </div>
            )}
          </div>

          {/* RIGHT — summary */}
          <aside className="lg:sticky lg:top-28 self-start rounded-3xl bg-navy text-cream p-7 shadow-2xl shadow-navy/20 overflow-hidden relative">
            <div className="absolute inset-0 pattern-girih opacity-15" />
            <div className="relative space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Your donation</div>
                <div className="font-serif text-5xl mt-2">${coverFees ? totalWithFees : effectiveAmount.toFixed(2)}</div>
                <div className="text-xs text-cream/70 mt-1">
                  {FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.label} · {designation}
                </div>
              </div>
              <div className="pt-4 border-t border-cream/15 space-y-2 text-xs text-cream/75">
                <div className="flex gap-2"><Shield size={13} className="text-gold shrink-0 mt-0.5" /><span>Secured by Stripe · card + bank (ACH) supported</span></div>
                <div className="flex gap-2"><Heart size={13} className="text-gold shrink-0 mt-0.5" /><span>Tax-deductible · EIN on receipt</span></div>
                {frequency !== "one_time" && (
                  <div className="flex gap-2"><Repeat size={13} className="text-gold shrink-0 mt-0.5" /><span>Cancel anytime from your receipt email</span></div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function SectionTitle({ num, children }) {
  return (
    <h2 className="font-serif text-2xl text-ink flex items-center gap-3">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gold text-navy text-xs font-bold">{num}</span>
      {children}
    </h2>
  );
}

function InlineCheckout({ mode, txId, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setError(submitErr.message || "Please complete all fields");
      setSubmitting(false);
      return;
    }
    const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: undefined,
      confirmParams: {
        // Apple Pay / Google Pay / 3DS auth flows that DO redirect end up here
        // — we mark `instant=1` so the success page knows Stripe already
        // confirmed and can skip polling entirely.
        return_url: `${window.location.origin}/donate/success?tx=${txId}&instant=1`,
      },
      redirect: "if_required",
    });
    if (confirmErr) {
      setError(confirmErr.message || "Payment failed");
      setSubmitting(false);
      return;
    }
    // Stripe confirmed without a redirect (most card / Apple Pay flows).
    // We have the PaymentIntent in hand — pass its status straight to the
    // success page so it can show "Thank you" instantly, no polling needed.
    const piStatus = paymentIntent?.status || "succeeded";
    const piId = paymentIntent?.id || "";
    navigate(`/donate/success?tx=${txId}&instant=1&pi_status=${piStatus}&pi=${piId}`);
  };

  return (
    <form onSubmit={onSubmit} data-testid="inline-checkout">
      <div className="rounded-3xl bg-white border border-sand p-6 md:p-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-3">Secure payment</div>
        <h2 className="font-serif text-2xl text-ink mb-5">
          {mode === "subscription" ? "Set up recurring donation" : "Complete your donation"}
        </h2>
        <PaymentElement options={{ layout: "tabs" }} />
        {error && <div data-testid="payment-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || !stripe}
            data-testid="pay-now"
            className="flex-1 rounded-full bg-navy text-cream py-4 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-navy-900 disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-navy/20"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
            {mode === "subscription" ? "Confirm recurring donation" : "Donate now"}
          </button>
          <button type="button" onClick={onCancel} data-testid="cancel-inline" className="px-5 py-4 text-sm text-ink/60 hover:text-ink">Back</button>
        </div>
        <div className="mt-4 text-[11px] text-ink/50 text-center">Your bank / card info is encrypted and sent directly to Stripe — FIC never stores it.</div>
      </div>
    </form>
  );
}
